import React, { useEffect, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import { useMap } from 'react-leaflet';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { formatNumber } from 'utilities/utils';

import config from 'config/config';

const CustomReactPopup = ({ feature, selectedLayerSet, layer }) => {
    const { popupContents } = selectedLayerSet;

    // Extract popup content from the popupContents array based on type. The array popupContents has a popupType field that
    // identifies the type of popup content. We reduce that to objects named for the popupType so we can conditionally render
    // them in the return statement below.
    const {
        title,
        health,
        environment,
        secondary,
        additional,
        link,
    } = popupContents.reduce((acc, curr) => {
        acc[curr.popupType] = curr;
        return acc;
    }, {});

    const checkAvailability = (value, unit = '', field = null) => {
        if (value === 0) {
            return '0.0';
        }
        if (!value) {
            return '';
        }
        if (value === config.missing) {
            return 'Missing';
        }
        if (value === config.suppressed) {
            return 'Suppressed';
        }
        // Add .0 to whole numbers if it's a percentage rate
        if (unit === '%' || field.toLowerCase().includes('rate')) {
            return `${formatNumber(value)}${unit}`;
        }
        return `${value.toLocaleString()}${unit}`;
    };

    // Popup for non-reference layers:
    if (!layer.referenceLayer) {
        return (
            <>
                <Typography>
                    <div className="popup-title">
                        {feature[title.field]?.toLowerCase()}{' '}
                    </div>
                </Typography>
                {health ? (
                    <Typography className="popup-health">
                        <div className="popup-data-container">
                            <div className="field-header">{health.name}:</div>{' '}
                            <div className="field-value">
                                {checkAvailability(
                                    feature[health.field],
                                    health.unit,
                                    health.field || ''
                                )}
                            </div>
                        </div>
                    </Typography>
                ) : null}
                {environment ? (
                    <Typography className="popup-environment">
                        <div className="popup-data-container">
                            <div className="field-header">
                                {environment.name}:
                            </div>{' '}
                            <div className={environment.className}>
                                {checkAvailability(
                                    feature[environment.field],
                                    environment.unit,
                                    environment.field || ''
                                )}
                            </div>
                        </div>
                    </Typography>
                ) : null}
                {secondary ? (
                    <Typography className="popup-environment">
                        <div className="popup-data-container">
                            <div className="field-header">
                                {secondary.name}:
                            </div>{' '}
                            <div className={environment.className}>
                                <ul className="secondary-ul">
                                    {checkAvailability(
                                        feature[secondary.field],
                                        secondary.unit,
                                        secondary.field || ''
                                    )
                                        .split(',')
                                        .map((item) => (
                                            <li
                                                key={item}
                                                className="secondary-li"
                                            >
                                                {item}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        </div>
                    </Typography>
                ) : null}
                {additional ? (
                    <Typography className="popup-environment">
                        <div className="popup-data-container">
                            <div className="field-header">
                                {additional.name}
                            </div>{' '}
                            <div className="field-value-align-left">
                                {checkAvailability(
                                    feature[additional.field],
                                    additional.unit,
                                    additional.field || ''
                                )}
                            </div>
                        </div>
                    </Typography>
                ) : null}
                {link ? (
                    <Typography className="popup-environment">
                        <div className="popup-data-container">
                            <div className="field-value-link">
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={checkAvailability(
                                        feature[link.field],
                                        link.unit,
                                        link.field || ''
                                    )}
                                >
                                    Consumer Confidence Report
                                </a>
                            </div>
                        </div>
                    </Typography>
                ) : null}
                {!layer.omitCommunityProfile ? (
                    <div className="demographic-data-link-container">
                        <a
                            href="#community-profile"
                            className="unset-anchor-styles demographic-data-link"
                        >
                            See demographic data
                            <KeyboardArrowDownIcon fontSize="small" />
                        </a>
                    </div>
                ) : null}
            </>
        );
    }

    const {
        title: referenceTitle,
        customTitle,
        line1,
        line2A,
        line2B,
        line3,
    } = layer.content;

    // Popup for reference layers:
    return (
        <div className="reference-layer-popup">
            <Typography>
                <div className="popup-title">
                    {customTitle || feature[referenceTitle]}
                </div>
            </Typography>
            <Typography>
                <div className="reference-value">
                    {feature[line1] || line1}
                    <br />
                    <div className="popup-line-2-container">
                        {formatNumber(feature[line2A])}
                        {line2B}
                    </div>
                    {feature[line3]}
                </div>
            </Typography>
        </div>
    );
};

const LayerComp = ({
    layer,
    selectedLayerSet,
    sourceDataRef,
    mapZoom,
    setMapLayers,
    setLayerExtent,
    setLayersLoading,
    setMapDemographicContent,
    currentTab,
}) => {
    // // Leaflet Hook // //
    const map = useMap();

    const isCancelled = useRef();
    const layerExists = useRef(false);

    // // Methods // //

    // // Effects // //

    // Note: linter wants unchanging useState setter functions setLayerExtent, setLayersLoading, and setMapDemographicContent (passed as props to this component) to be included in the dependency array -- will disable warning in line before deps array below
    useEffect(() => {
        // This method removes previous feature layers before rendering a new one, needed for changing layers via time slider.
        const clearFeatureLayers = () => {
            map.eachLayer((mapLayer) => {
                // eslint-disable-next-line no-underscore-dangle
                if (!mapLayer._tiles) {
                    // This prevents the base maps from being removed when clearing old maps during timeslider change event. Only basemaps contain the _tiles key.
                    map.removeLayer(mapLayer);
                }
            });
        };

        const setLayer = () => {
            // Set isCancelled to false when this component "mounts"
            isCancelled.current = false;

            layer.esriLayer.on({
                loading: () => {
                    // When first adding the layer to the map, layerExists.current should be false (initialized false in useRef hook). But should this layer attempt to load again, after it has loaded once before (as it does when panning/zooming), then this will *reset* the ref to false when the load begins but before it is complete.
                    layerExists.current = false;

                    // Add this layer to the layersLoading array in HealthTopic.js state.
                    // (NOTE: Must use callback to get the most up-to-date state variable. Accessing layersLoading from anywhere outside of that callback will return a stale value)
                    setLayersLoading((layersLoading) => [
                        ...layersLoading,
                        layer.id,
                    ]);
                },
                load: (e) => {
                    // Remove this layer from the layersLoading array in HealthTopic.js state.
                    setLayersLoading((layersLoading) =>
                        layersLoading.filter((id) => id !== layer.id)
                    );

                    // Grab an arbitrary feature to set "layerYear" field in topics.js config file for current loaded layer:
                    const sampleFeatureProperties = Object.values(
                        // eslint-disable-next-line no-underscore-dangle
                        e.target._layers
                    )[0]?.feature?.properties; // Verifying that all properties exist before continuing.

                    const [
                        currentLayerMetaData,
                    ] = selectedLayerSet.setLayers.filter(
                        (set) => set.id === e.target.options.id
                    );
                    const { yearKey } = currentLayerMetaData;

                    const layerYear = sampleFeatureProperties
                        ? sampleFeatureProperties[yearKey]
                        : null;

                    currentLayerMetaData.layerYear = layerYear; // Note: this will mutate the topics.js object

                    // Set this ref to true so we can identify that the layer exists in the map later during cleanup:
                    layerExists.current = true;

                    if (isCancelled.current) {
                        // If this on load event handler runs after this component is destroyed, will remove layer here instead of in effect's cleanup function. (Running layer.remove() too early, e.g. after the layer begins loading but before the layer has fully loaded, will not successfully remove the layer).
                        layer.esriLayer.remove();
                    } else {
                        // Stack layers according to zIndex set in topics.js
                        const { zIndexPosition } = e.target.options;
                        if (zIndexPosition === 'back') {
                            layer.esriLayer.bringToBack();
                        }
                        if (zIndexPosition === 'front') {
                            layer.esriLayer.bringToFront();
                        }

                        // Set table data if layer is our "sourceLayer", the layer set up in the ArcGIS service that joins all relevant layers
                        // Also check if the layer fetch has been cancelled
                        if (
                            currentTab.mapSettings.timeSlider ||
                            (e.target.options.sourceLayer &&
                                !isCancelled.current)
                        ) {
                            sourceDataRef.current = e.target;
                            setLayerExtent();
                        }

                        // Bind popup if not already bound:
                        // eslint-disable-next-line no-underscore-dangle
                        if (!e.target._popup) {
                            layer.esriLayer
                                .bindPopup(
                                    ({ feature }) =>
                                        renderToString(
                                            <CustomReactPopup
                                                feature={feature.properties}
                                                selectedLayerSet={
                                                    selectedLayerSet
                                                }
                                                layer={layer.esriLayer.options}
                                            />
                                        ),
                                    { maxWidth: 'auto' }
                                )
                                .on('popupopen', ({ layer: { feature } }) =>
                                    layer.esriLayer.options.omitCommunityProfile
                                        ? null
                                        : setMapDemographicContent(feature)
                                )
                                .on('popupclose', () => {
                                    setMapDemographicContent(null);
                                });
                        }
                    }
                },
            });

            if (currentTab.mapSettings.timeSlider) {
                layer.esriLayer.setStyle(() => ({
                    smoothFactor: 0,
                }));
            }

            // Only add layer here if layer is not scale dependent,
            // (if it is, will be added by zoomCheck effect).
            if (!layer.esriLayer.options.minScale) {
                map.whenReady(() => {
                    map.addLayer(layer.esriLayer);
                });
            }
        };
        if (currentTab.mapSettings.timeSlider) {
            clearFeatureLayers();
        }
        setLayer();
        return function cleanup() {
            // Set isCancelled to true to signal that this component has been destroyed and we should cancel any pending on load handlers
            isCancelled.current = true;

            // Remove layer only if it successfully loaded before this cleanup was run.
            // (If layer does not yet exist, removal will take place within on load handler for this layer)
            if (layerExists.current) {
                layer.esriLayer.remove();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layer.esriLayer.options.id, layer, sourceDataRef, map]);

    // Effect to update this layer if zoom changes
    useEffect(() => {
        const zoomCheck = () => {
            if (layer.esriLayer.options.scaleDependent) {
                if (
                    mapZoom < layer.esriLayer.options.minScale &&
                    map.hasLayer(layer.esriLayer)
                ) {
                    setMapLayers((prevMapLayers) => {
                        prevMapLayers.find(
                            (mapLayer) => mapLayer.id === layer.id
                        ).outOfRange = true;
                        return prevMapLayers;
                    });
                    layer.esriLayer.remove();
                } else if (
                    mapZoom >= layer.esriLayer.options.minScale &&
                    !map.hasLayer(layer.esriLayer)
                ) {
                    setMapLayers((prevMapLayers) => {
                        prevMapLayers.find(
                            (mapLayer) => mapLayer.id === layer.id
                        ).outOfRange = false;
                        return prevMapLayers;
                    });
                    layer.esriLayer.addTo(map);
                }
            }
        };
        zoomCheck();
    }, [layer.esriLayer, layer.id, map, mapZoom, setMapLayers]);

    // // Render // //
    return null;
};

export default LayerComp;
